import * as THREE from "three";
import { cheapTextures, normalMapFrom, roughFrom } from "./mapTextures.js";

/**
 * Procedural, tileable MONSTER surfaces — the fifth member of the family that
 * already covers the maps (`mapTextures`), the avatars (`avatarTextures`), the
 * guns (`weaponTextures`) and the vehicles (`vehicleTextures`). Added
 * 2026-08-01 (mission `graphics`).
 *
 * Until now every monster in the game — godzilla, kong, the alien, the kraken,
 * the loot goblin — was built from `MeshStandardMaterial({ color, roughness })`
 * with NO map at all, on axis-aligned `BoxGeometry`. A 6 m torso and a 0.3 m
 * eye caught light identically, so the biggest object the game ever puts on
 * screen was also the flattest. Scaly reptile hide, matted ape fur, wet
 * cephalopod skin and polished chitin are exactly the surfaces that carry the
 * read at 40 m, which is the only distance you ever see a kaiju from.
 *
 * Contract: same as `vehicleMaps` and NOT `weaponMaps` — the detail is
 * composited over WHITE and `params.color` is left alone, so `color × map`
 * reproduces the authored hue plus relief. One albedo per family for the whole
 * game (a godzilla and a kong share nothing, but the goblin's sack and the
 * kraken's suckers each reuse a family), so the set is built once per tab.
 *
 * 2026-08-01, the BEAST pass: `serp` and `grit` joined the set for the sky
 * dragon and the sandworm in `worldEntities.ts`. Those two are not monsters in
 * the `monsters.ts` sense, but they are creatures built on the same toolkit
 * (`creature.ts`), so they wear the same families rather than a parallel set.
 *
 * UVs: call sites run `worldProjectUV(geo, tile)`, so scale rows are the same
 * size on a 6 m flank as on a 0.9 m forearm — per-face 0..1 UVs could never do
 * that, and it is why the old monsters had no legible surface scale at all.
 */

const ALBEDO = 256;
const ROUGH = 128;

export type MonsterSurface =
  | "scale"   // reptile hide — godzilla
  | "fur"     // matted ape hair — kong
  | "chitin"  // polished insect plate — alien
  | "ceph"    // wet papillose squid skin — kraken
  | "hide"    // warty leather — goblin
  | "flesh"   // soft pored skin — the penis npc, kong's face, goblin's ears
  | "horn"    // keratin — claws, teeth, beak, tusks
  | "burlap"  // woven sack / rag cloth
  | "serp"    // big lamellar serpent scales, metallic-edged — the sky dragon
  | "grit";   // sand-caked segmented carapace — the sandworm

interface Family {
  map: THREE.CanvasTexture | null;
  normal: THREE.CanvasTexture | null;
  rough: THREE.CanvasTexture | null;
}

function cvs(size: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  return { c, g: c.getContext("2d", { willReadFrequently: true })! };
}

/** Per-pixel grain straight into the ImageData — a fillRect per sample costs
 *  hundreds of ms in canvas state changes (same reason as `mapTextures`). */
function speckle(g: CanvasRenderingContext2D, n: number, alpha: number, lo: number, hi: number): void {
  const img = g.getImageData(0, 0, ALBEDO, ALBEDO);
  const d = img.data;
  for (let i = 0; i < n; i++) {
    const j = (((Math.random() * ALBEDO) | 0) + ((Math.random() * ALBEDO) | 0) * ALBEDO) * 4;
    const v = lo + Math.random() * (hi - lo);
    d[j] += (v - d[j]) * alpha;
    d[j + 1] += (v - d[j + 1]) * alpha;
    d[j + 2] += (v - d[j + 2]) * alpha;
  }
  g.putImageData(img, 0, 0);
}

/** A dome of height — the primitive every bump on an organic surface is made
 *  of (a scale, a wart, a papilla, a knuckle). Lit from the upper left so the
 *  albedo gets a matching shade even where the normal map is dropped
 *  (`cheapTextures`). */
function dome(g: CanvasRenderingContext2D, x: number, y: number, r: number, hi: number, lo = 0): void {
  const gr = g.createRadialGradient(x - r * 0.3, y - r * 0.3, r * 0.05, x, y, r);
  gr.addColorStop(0, `rgba(255,255,255,${hi})`);
  gr.addColorStop(0.6, `rgba(150,150,150,${hi * 0.35})`);
  gr.addColorStop(1, `rgba(0,0,0,${lo})`);
  g.fillStyle = gr;
  g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
}

/** Wandering line — creases, wrinkles, hair, grain. Wraps horizontally by
 *  construction (drawn twice, offset by the tile) so the seam is invisible. */
function wander(
  g: CanvasRenderingContext2D, x0: number, y0: number, len: number, ang: number,
  jitter: number, w: number, col: string,
): void {
  g.strokeStyle = col; g.lineWidth = w;
  g.beginPath();
  let x = x0, y = y0, a = ang;
  g.moveTo(x, y);
  const steps = Math.max(2, len / 4);
  for (let i = 0; i < steps; i++) {
    a += (Math.random() - 0.5) * jitter;
    x += Math.cos(a) * 4; y += Math.sin(a) * 4;
    g.lineTo(x, y);
  }
  g.stroke();
}

/** `a` draws the albedo (over white), `h` draws the height field (over grey). */
type Draw = (a: CanvasRenderingContext2D, h: CanvasRenderingContext2D) => void;

/** REPTILE HIDE — overlapping scale rows, each row offset half a scale, with
 *  a scattering of bigger keeled osteoderms. This is godzilla's whole read. */
const drawScale: Draw = (a, h) => {
  const R = 13;          // scale radius
  const stepY = R * 1.35;
  for (let row = 0, y = 0; y < ALBEDO + stepY; row++, y += stepY) {
    const off = (row % 2) * R;
    for (let x = -R; x < ALBEDO + R * 2; x += R * 2) {
      const cx = x + off, cy = y;
      const rr = R * (0.86 + Math.random() * 0.22);
      // the scale body: a dome with a dark overlap shadow under its lower edge
      for (const g of [a, h]) {
        g.save();
        g.beginPath();
        g.ellipse(cx, cy, rr, rr * 0.78, 0, 0, 7);
        g.clip();
        dome(g, cx, cy - rr * 0.12, rr * 1.15, g === h ? 0.85 : 0.30, g === h ? 0.5 : 0.22);
        g.restore();
      }
      a.strokeStyle = "rgba(0,0,0,0.30)"; a.lineWidth = 1.3;
      a.beginPath(); a.ellipse(cx, cy, rr, rr * 0.78, 0, 0.15, Math.PI - 0.15); a.stroke();
      h.strokeStyle = "rgba(0,0,0,0.75)"; h.lineWidth = 2.2;
      h.beginPath(); h.ellipse(cx, cy, rr, rr * 0.78, 0, 0.1, Math.PI - 0.1); h.stroke();
    }
  }
  // keeled osteoderms — the coarse second frequency that stops it reading as
  // a regular grid from 40 m
  for (let i = 0; i < 26; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 8 + Math.random() * 9;
    dome(h, x, y, r, 0.85, 0.35);
    a.fillStyle = "rgba(0,0,0,0.10)"; a.beginPath(); a.arc(x, y, r, 0, 7); a.fill();
    dome(a, x, y, r * 0.8, 0.22);
  }
  speckle(a, 22000, 0.13, 70, 170); // mottling between the scales
};

/** MATTED APE FUR — thousands of directional strands in clumps, plus the
 *  low-frequency parting that makes a pelt read as a pelt. */
const drawFur: Draw = (a, h) => {
  // clump shading first: broad darker channels where the hair parts
  for (let i = 0; i < 60; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 14 + Math.random() * 26;
    const gr = a.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, "rgba(0,0,0,0.20)");
    gr.addColorStop(1, "rgba(128,128,128,0)");
    a.fillStyle = gr; a.beginPath(); a.arc(x, y, r, 0, 7); a.fill();
    const gh = h.createRadialGradient(x, y, 0, x, y, r);
    gh.addColorStop(0, "rgba(0,0,0,0.35)");
    gh.addColorStop(1, "rgba(128,128,128,0)");
    h.fillStyle = gh; h.beginPath(); h.arc(x, y, r, 0, 7); h.fill();
  }
  // the strands: mostly downward (gravity), lightly splayed
  for (let i = 0; i < 5200; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const ang = Math.PI / 2 + (Math.random() - 0.5) * 0.9;
    const len = 9 + Math.random() * 22;
    const lit = Math.random() < 0.4;
    wander(a, x, y, len, ang, 0.25, 0.9, lit ? "rgba(255,255,255,0.13)" : "rgba(0,0,0,0.20)");
    wander(h, x, y, len, ang, 0.25, 1.0, lit ? "rgba(255,255,255,0.30)" : "rgba(0,0,0,0.28)");
  }
  speckle(a, 18000, 0.10, 60, 150);
};

/** POLISHED CHITIN — segmented plates with a bevelled lip, micro pores and a
 *  faint iridescent sheen. Hard and wet-looking, the opposite of fur. */
const drawChitin: Draw = (a, h) => {
  // plate segmentation: 4 bands with a deep groove and a raised leading lip
  for (let i = 0; i < 4; i++) {
    const y = i * 64;
    a.fillStyle = "rgba(0,0,0,0.34)"; a.fillRect(0, y, ALBEDO, 5);
    h.fillStyle = "rgba(0,0,0,0.95)"; h.fillRect(0, y, ALBEDO, 5);
    a.fillStyle = "rgba(255,255,255,0.22)"; a.fillRect(0, y + 5, ALBEDO, 3);
    h.fillStyle = "rgba(255,255,255,0.55)"; h.fillRect(0, y + 5, ALBEDO, 4);
    // the plate itself bulges toward its trailing edge
    const gr = h.createLinearGradient(0, y + 8, 0, y + 64);
    gr.addColorStop(0, "rgba(255,255,255,0.22)");
    gr.addColorStop(0.7, "rgba(128,128,128,0)");
    gr.addColorStop(1, "rgba(0,0,0,0.20)");
    h.fillStyle = gr; h.fillRect(0, y + 8, ALBEDO, 56);
  }
  // longitudinal ridges
  for (const x of [40, 128, 214]) {
    a.fillStyle = "rgba(255,255,255,0.10)"; a.fillRect(x, 0, 4, ALBEDO);
    h.fillStyle = "rgba(255,255,255,0.30)"; h.fillRect(x, 0, 4, ALBEDO);
  }
  // pores
  for (let i = 0; i < 900; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 0.7 + Math.random() * 1.7;
    h.fillStyle = "rgba(0,0,0,0.5)"; h.beginPath(); h.arc(x, y, r, 0, 7); h.fill();
    a.fillStyle = "rgba(0,0,0,0.16)"; a.beginPath(); a.arc(x, y, r, 0, 7); a.fill();
  }
  // iridescence: a broad two-tone sweep, subtle enough to survive the tint
  const sheen = a.createLinearGradient(0, 0, ALBEDO, ALBEDO * 0.6);
  sheen.addColorStop(0, "rgba(150,255,220,0.16)");
  sheen.addColorStop(0.5, "rgba(255,255,255,0.02)");
  sheen.addColorStop(1, "rgba(190,160,255,0.16)");
  a.fillStyle = sheen; a.fillRect(0, 0, ALBEDO, ALBEDO);
};

/** CEPHALOPOD SKIN — soft, wet, covered in papillae, with chromatophore
 *  freckles that clump the way a squid's pigment cells actually do. */
const drawCeph: Draw = (a, h) => {
  for (let i = 0; i < 1500; i++) { // papillae
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 1.5 + Math.random() * 4;
    dome(h, x, y, r, 0.55, 0.18);
    dome(a, x, y, r, 0.14, 0.08);
  }
  for (let c = 0; c < 90; c++) { // chromatophore clusters
    const cx = Math.random() * ALBEDO, cy = Math.random() * ALBEDO;
    const n = 4 + (Math.random() * 9) | 0;
    for (let i = 0; i < n; i++) {
      const x = cx + (Math.random() - 0.5) * 26, y = cy + (Math.random() - 0.5) * 26;
      a.fillStyle = `rgba(${Math.random() < 0.5 ? "20,8,32" : "70,20,60"},${0.14 + Math.random() * 0.3})`;
      a.beginPath(); a.arc(x, y, 1.2 + Math.random() * 3.4, 0, 7); a.fill();
    }
  }
  // slack wrinkles running with the mantle
  for (let i = 0; i < 40; i++) {
    const y = Math.random() * ALBEDO;
    wander(a, -10, y, ALBEDO + 20, 0, 0.10, 1.4, "rgba(0,0,0,0.13)");
    wander(h, -10, y, ALBEDO + 20, 0, 0.10, 1.8, "rgba(0,0,0,0.22)");
  }
  speckle(a, 14000, 0.09, 120, 210);
};

/** WARTY LEATHER — the goblin. Coarse creases in a network, knobbly warts,
 *  grubby speckle. Reads as "small nasty creature" rather than "green box". */
const drawHide: Draw = (a, h) => {
  for (let i = 0; i < 55; i++) { // crease network
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const ang = Math.random() * Math.PI * 2;
    wander(a, x, y, 30 + Math.random() * 70, ang, 0.45, 1.5, "rgba(0,0,0,0.20)");
    wander(h, x, y, 30 + Math.random() * 70, ang, 0.45, 2.2, "rgba(0,0,0,0.45)");
  }
  for (let i = 0; i < 260; i++) { // warts
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 2 + Math.random() * 5.5;
    dome(h, x, y, r, 0.8, 0.3);
    dome(a, x, y, r, 0.18, 0.14);
  }
  speckle(a, 26000, 0.14, 60, 150);
};

/** SOFT PORED SKIN — fine pores, a faint capillary web, gentle unevenness.
 *  Very low relief: this is the surface that must NOT look like leather. */
const drawFlesh: Draw = (a, h) => {
  for (let i = 0; i < 30; i++) { // subcutaneous unevenness
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 18 + Math.random() * 30;
    const gr = h.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, Math.random() < 0.5 ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)");
    gr.addColorStop(1, "rgba(128,128,128,0)");
    h.fillStyle = gr; h.beginPath(); h.arc(x, y, r, 0, 7); h.fill();
  }
  for (let i = 0; i < 24; i++) { // capillaries
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    wander(a, x, y, 24 + Math.random() * 50, Math.random() * 7, 0.5, 0.9, "rgba(170,60,80,0.14)");
  }
  for (let i = 0; i < 2600; i++) { // pores
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 0.5 + Math.random() * 1.3;
    h.fillStyle = "rgba(0,0,0,0.35)"; h.beginPath(); h.arc(x, y, r, 0, 7); h.fill();
    a.fillStyle = "rgba(0,0,0,0.07)"; a.beginPath(); a.arc(x, y, r, 0, 7); a.fill();
  }
  speckle(a, 12000, 0.07, 170, 240);
};

/** KERATIN — claws, teeth, beak. Lengthwise striations, a polished tip sheen,
 *  a few chips. Deliberately the least uniform hard surface in the set. */
const drawHorn: Draw = (a, h) => {
  const sheen = a.createLinearGradient(0, 0, 0, ALBEDO);
  sheen.addColorStop(0, "rgba(255,255,255,0.22)");
  sheen.addColorStop(0.55, "rgba(120,110,100,0.14)");
  sheen.addColorStop(1, "rgba(40,36,32,0.20)");
  a.fillStyle = sheen; a.fillRect(0, 0, ALBEDO, ALBEDO);
  for (let i = 0; i < 220; i++) { // striations along the length (v axis)
    const x = Math.random() * ALBEDO;
    const lit = Math.random() < 0.45;
    wander(a, x, -8, ALBEDO + 16, Math.PI / 2, 0.05, 1.1, lit ? "rgba(255,255,255,0.13)" : "rgba(0,0,0,0.16)");
    wander(h, x, -8, ALBEDO + 16, Math.PI / 2, 0.05, 1.4, lit ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.26)");
  }
  for (let i = 0; i < 26; i++) { // chips
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 2 + Math.random() * 5;
    h.fillStyle = "rgba(0,0,0,0.55)"; h.beginPath(); h.arc(x, y, r, 0, 7); h.fill();
    a.fillStyle = "rgba(0,0,0,0.13)"; a.beginPath(); a.arc(x, y, r, 0, 7); a.fill();
  }
  speckle(a, 9000, 0.10, 150, 230);
};

/** BURLAP — the goblin's loot sack and its ragged wrap. A real over/under
 *  weave, frayed slubs, a couple of pulled threads. */
const drawBurlap: Draw = (a, h) => {
  const P = 8; // thread pitch
  for (let y = 0; y < ALBEDO; y += P) {
    for (let x = 0; x < ALBEDO; x += P) {
      const warpOver = ((x / P + y / P) | 0) % 2 === 0;
      const g0 = warpOver ? a : a, hh = h;
      // the over-thread is lit and raised; the under-thread is shaded
      if (warpOver) {
        g0.fillStyle = "rgba(255,255,255,0.16)"; g0.fillRect(x, y + 1, P, P - 2);
        hh.fillStyle = "rgba(255,255,255,0.42)"; hh.fillRect(x, y + 1, P, P - 2);
      } else {
        g0.fillStyle = "rgba(0,0,0,0.16)"; g0.fillRect(x + 1, y, P - 2, P);
        hh.fillStyle = "rgba(255,255,255,0.42)"; hh.fillRect(x + 1, y, P - 2, P);
      }
      hh.fillStyle = "rgba(0,0,0,0.35)"; hh.fillRect(x, y, 1.4, P); // inter-thread gap
      hh.fillRect(x, y, P, 1.4);
    }
  }
  for (let i = 0; i < 130; i++) { // slubs and loose fibres
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    wander(a, x, y, 6 + Math.random() * 16, Math.random() * 7, 0.6, 1.1, "rgba(0,0,0,0.18)");
  }
  speckle(a, 20000, 0.12, 90, 190);
};

/** LAMELLAR SERPENT SCALE — the sky dragon. Deliberately NOT `scale`: a
 *  chinese-style dragon wears big diamond plates in diagonal courses with a
 *  raised central keel and a bright metal rim, an order of magnitude coarser
 *  than godzilla's pebbled hide. That coarseness is the entire reason the
 *  dragon reads at the 60-80 m it is usually seen from. */
const drawSerp: Draw = (a, h) => {
  const S = 32;                      // diamond half-diagonal
  for (let row = 0, y = -S; y < ALBEDO + S; row++, y += S) {
    const off = (row % 2) * S;
    for (let x = -S; x < ALBEDO + S; x += S * 2) {
      const cx = x + off, cy = y;
      const w = S * 0.98, hh = S * 0.62;
      const dia = (g: CanvasRenderingContext2D): void => {
        g.beginPath();
        g.moveTo(cx, cy - hh); g.lineTo(cx + w, cy); g.lineTo(cx, cy + hh); g.lineTo(cx - w, cy);
        g.closePath();
      };
      // plate body — domed, so it catches a rim light along the upper edge
      for (const g of [a, h]) {
        g.save(); dia(g); g.clip();
        dome(g, cx, cy - hh * 0.25, S * 1.05, g === h ? 0.9 : 0.26, g === h ? 0.55 : 0.20);
        g.restore();
      }
      // raised central keel down the long axis
      for (const g of [a, h]) {
        g.strokeStyle = g === h ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.20)";
        g.lineWidth = g === h ? 3.2 : 2.2;
        g.beginPath(); g.moveTo(cx - w * 0.75, cy); g.lineTo(cx + w * 0.75, cy); g.stroke();
      }
      // bright metal rim on top, deep shadow gap below (the overlap)
      a.strokeStyle = "rgba(255,255,255,0.34)"; a.lineWidth = 1.6;
      a.beginPath(); a.moveTo(cx - w, cy); a.lineTo(cx, cy - hh); a.lineTo(cx + w, cy); a.stroke();
      a.strokeStyle = "rgba(0,0,0,0.42)"; a.lineWidth = 2.4;
      a.beginPath(); a.moveTo(cx - w, cy); a.lineTo(cx, cy + hh); a.lineTo(cx + w, cy); a.stroke();
      h.strokeStyle = "rgba(0,0,0,0.9)"; h.lineWidth = 3;
      h.beginPath(); h.moveTo(cx - w, cy); h.lineTo(cx, cy + hh); h.lineTo(cx + w, cy); h.stroke();
    }
  }
  // hairline cracks + a little tarnish so the metal isn't uniform
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    wander(a, x, y, 8 + Math.random() * 20, Math.random() * 7, 0.5, 0.9, "rgba(0,0,0,0.16)");
  }
  speckle(a, 14000, 0.10, 110, 215);
};

/** SAND-CAKED CARAPACE — the sandworm. Coarse desert grain packed into the
 *  gaps of a hard segmented shell: horizontal growth ridges, a scatter of
 *  embedded pebbles, and a dusting of loose sand that sits brighter than the
 *  plate it clings to. The worm spends its life underground; a clean plate
 *  would look wrong. */
const drawGrit: Draw = (a, h) => {
  // growth ridges across the segment — the low frequency
  for (let i = 0; i < 7; i++) {
    const y = (i / 7) * ALBEDO + Math.random() * 6;
    const t = 3 + Math.random() * 5;
    a.fillStyle = "rgba(0,0,0,0.26)"; a.fillRect(0, y, ALBEDO, t);
    h.fillStyle = "rgba(0,0,0,0.85)"; h.fillRect(0, y, ALBEDO, t);
    a.fillStyle = "rgba(255,255,255,0.24)"; a.fillRect(0, y + t, ALBEDO, 2.5);
    h.fillStyle = "rgba(255,255,255,0.55)"; h.fillRect(0, y + t, ALBEDO, 3.5);
  }
  // embedded pebbles / grit nodules, two sizes
  for (const [n, lo, hi] of [[190, 2.5, 6], [46, 6, 12]] as const) {
    for (let i = 0; i < n; i++) {
      const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = lo + Math.random() * (hi - lo);
      dome(h, x, y, r, 0.8, 0.3);
      a.fillStyle = "rgba(0,0,0,0.13)"; a.beginPath(); a.arc(x, y, r, 0, 7); a.fill();
      dome(a, x, y, r * 0.85, 0.30);
    }
  }
  // wind-scour striations along the burrow axis
  for (let i = 0; i < 150; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    wander(a, x, y, 14 + Math.random() * 30, (Math.random() - 0.5) * 0.35, 0.18, 1.2,
      Math.random() < 0.5 ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.14)");
  }
  speckle(a, 30000, 0.18, 60, 220); // the loose sand itself: high-contrast grain
  speckle(h, 9000, 0.10, 90, 180);
};

const SPECS: Record<MonsterSurface, { draw: Draw; bump: number; roughLo: number; roughHi: number }> = {
  scale:  { draw: drawScale,  bump: 2.6, roughLo: 0.42, roughHi: 0.88 },
  fur:    { draw: drawFur,    bump: 2.2, roughLo: 0.62, roughHi: 0.98 },
  chitin: { draw: drawChitin, bump: 1.8, roughLo: 0.10, roughHi: 0.44 },
  ceph:   { draw: drawCeph,   bump: 1.9, roughLo: 0.16, roughHi: 0.52 },
  hide:   { draw: drawHide,   bump: 2.4, roughLo: 0.50, roughHi: 0.92 },
  flesh:  { draw: drawFlesh,  bump: 1.0, roughLo: 0.32, roughHi: 0.62 },
  horn:   { draw: drawHorn,   bump: 1.6, roughLo: 0.18, roughHi: 0.58 },
  burlap: { draw: drawBurlap, bump: 2.8, roughLo: 0.72, roughHi: 0.99 },
  serp:   { draw: drawSerp,   bump: 3.0, roughLo: 0.14, roughHi: 0.56 },
  grit:   { draw: drawGrit,   bump: 2.7, roughLo: 0.66, roughHi: 0.99 },
};

// Built INCREMENTALLY into a persistent partial map (one family per build) so
// the ~3 s full generation can be pumped a family at a time from an idle-time
// scheduler (see main.ts warmDeferredTextures) instead of freezing the UI in
// one block. `all()` and `monsterMaps` are unchanged backstops that force
// whatever is still missing on demand; every path is idempotent + memoised.
const families: Partial<Record<MonsterSurface, Family>> = {};

function mk(canvas: HTMLCanvasElement, srgb: boolean): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.userData.shared = true; // module-cached: never disposed with a monster
  return t;
}

let buildMs = 0; // accumulated across incremental slices, for the one-time log
let warmLogged = false;

/** Generate (and cache) exactly ONE family. Byte-identical to the old loop
 *  body — same draw over white albedo / grey height, same normal + roughness
 *  derivation, same `cheap` (lowSpec) gate. */
function famOf(k: MonsterSurface): Family {
  const hit = families[k];
  if (hit) return hit;
  const t0 = performance.now();
  const cheap = cheapTextures();
  const spec = SPECS[k];
  const { c: ac, g: ag } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  ag.fillStyle = "#ffffff"; ag.fillRect(0, 0, ALBEDO, ALBEDO); // detail over WHITE: colour still tints
  hg.fillStyle = "#808080"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  spec.draw(ag, hg);
  const fam: Family = {
    map: mk(ac, true),
    normal: cheap ? null : mk(normalMapFrom(hc, spec.bump, ALBEDO), false),
    rough: cheap ? null : mk(roughFrom(hc, spec.roughLo, spec.roughHi, ROUGH), false),
  };
  families[k] = fam;
  buildMs += performance.now() - t0;
  if (!warmLogged && (Object.keys(SPECS) as MonsterSurface[]).every((s) => families[s])) {
    warmLogged = true;
    console.log(`[montex] ${Object.keys(SPECS).length} monster families warm in ${buildMs.toFixed(0)} ms`);
  }
  return fam;
}

function all(): Record<MonsterSurface, Family> {
  for (const k of Object.keys(SPECS) as MonsterSurface[]) famOf(k);
  return families as Record<MonsterSurface, Family>;
}

/** GPU-upload every monster family texture NOW (under the load screen), given
 *  the renderer's warm callback. A kaiju spawns in the MIDDLE of a round — the
 *  worst moment to pay a glTexImage2D upload on first draw — so move it here.
 *  Idempotent; cycle-free (the callback comes from the renderer). */
export function warmMonsterTextures(warm: (t: THREE.Texture) => void): void {
  const fams = all();
  for (const k of Object.keys(fams) as MonsterSurface[]) {
    const f = fams[k];
    if (f.map) warm(f.map);
    if (f.normal) warm(f.normal);
    if (f.rough) warm(f.rough);
  }
  for (const k of Object.keys(EYES) as EyeKind[]) warm(eyeTexture(k));
}

/** Build ONE not-yet-generated family and return whether any still remain, so
 *  an idle scheduler can pump the ~3 s job slice-by-slice (~300 ms/slice) and
 *  keep the menu responsive. No-op (returns false) once everything is warm. */
export function warmOneMonsterFamily(): boolean {
  const keys = Object.keys(SPECS) as MonsterSurface[];
  const next = keys.find((k) => !families[k]);
  if (next === undefined) return false;
  famOf(next);
  return keys.some((k) => !families[k]);
}

/** Shared normalScale vectors — never allocated per material. */
const normalScales = new Map<string, THREE.Vector2>();

/**
 * Fold a monster surface's albedo/normal/roughness into MeshStandardMaterial
 * params. Like `vehicleMaps` (and unlike `weaponMaps`) this does NOT touch
 * `params.color` — the authored monster hue keeps tinting the shared white
 * detail, so a violet kraken and a green goblin can share one family.
 */
export function monsterMaps(
  kind: MonsterSurface,
  params: THREE.MeshStandardMaterialParameters,
  strength = 1,
): THREE.MeshStandardMaterialParameters {
  const fam = famOf(kind);
  params.map = fam.map ?? undefined;
  if (fam.normal) {
    params.normalMap = fam.normal;
    const sk = `${kind}|${strength}`;
    let s = normalScales.get(sk);
    if (!s) { s = new THREE.Vector2(strength, strength); normalScales.set(sk, s); }
    params.normalScale = s;
  }
  if (fam.rough) params.roughnessMap = fam.rough;
  return params;
}

/** Build every family now. Called from the map-load warm-up so the first
 *  monster spawn of a match never pays the canvas build mid-fight — a kaiju
 *  spawns in the middle of a round, which is the worst possible moment. */
export function prewarmMonsterTextures(): void {
  all();
  for (const k of Object.keys(EYES) as EyeKind[]) eyeTexture(k);
}

/* ---------------------------------------------------------------------------
 * EYES (2026-10-23, the "realistic mobs" pass).
 *
 * Every monster eye used to be a flat-coloured sphere: an amber ball for
 * godzilla, a black bead for kong, a gold ball for the goblin, and for the
 * kraken a four-mesh stack (sclera + iris sphere + pupil BOX + highlight
 * sphere). Nothing read as an EYE, because a real eye's read is the iris
 * pattern and the pupil shape (a reptile's vertical slit, an ape's round
 * pupil in a dark sclera), not its colour.
 *
 * One small canvas per species, drawn as a FRONT-ON 2D picture of the eye. The
 * call site (`creature.ts` `eyePair`) gives the sphere PLANAR uvs along its
 * forward axis, so the picture lands undistorted on the front of the ball and
 * the texture border (sclera) wraps the sides. No polar pinch, no seam where
 * anyone can see it. 128² each, four of them: ~250 KB of GPU memory total.
 *
 * Glowing species use the SAME canvas as their emissiveMap, so the iris glows
 * and the pupil stays black — the old emissive-colour-only eyes glowed
 * uniformly, pupil and all.
 * ------------------------------------------------------------------------- */

export type EyeKind = "reptile" | "ape" | "goblin" | "squid";

interface EyeSpec {
  sclera: string;
  irisIn: string;     // around the pupil
  irisOut: string;    // toward the limbus
  limbus: string;     // the dark ring at the iris edge
  irisR: number;      // iris radius, fraction of the half-size
  pupil: "slit" | "round";
  pupilW: number;     // pupil half-width, fraction of irisR
  pupilH: number;     // pupil half-height, fraction of irisR
  veins: boolean;
}

const EYES: Record<EyeKind, EyeSpec> = {
  // godzilla: an all-iris amber reptile eye, vertical slit
  reptile: { sclera: "#6a3a08", irisIn: "#ffe08a", irisOut: "#d8740e", limbus: "#2a1404", irisR: 0.92, pupil: "slit", pupilW: 0.13, pupilH: 0.86, veins: false },
  // kong: a great ape — dark-brown sclera, brown iris, round pupil
  ape:     { sclera: "#3b2a20", irisIn: "#8a5a2c", irisOut: "#4a2e16", limbus: "#140c08", irisR: 0.5, pupil: "round", pupilW: 0.42, pupilH: 0.42, veins: true },
  // goblin: big coin-gold iris, round greedy pupil, bloodshot yellow sclera
  goblin:  { sclera: "#d8cc7a", irisIn: "#fff2a0", irisOut: "#ffb400", limbus: "#4a2a00", irisR: 0.66, pupil: "round", pupilW: 0.36, pupilH: 0.36, veins: true },
  // kraken: gold iris, the old vertical slit pupil, near-black sclera ring
  squid:   { sclera: "#1a0f28", irisIn: "#fff0a0", irisOut: "#ff9e2c", limbus: "#2a1206", irisR: 0.74, pupil: "slit", pupilW: 0.16, pupilH: 0.8, veins: false },
};

const eyeTex: Partial<Record<EyeKind, THREE.CanvasTexture>> = {};

/** The front-on eye picture for a species (module-cached, never disposed). */
export function eyeTexture(kind: EyeKind): THREE.CanvasTexture {
  const hit = eyeTex[kind];
  if (hit) return hit;
  const S = 128, c0 = S / 2;
  const spec = EYES[kind];
  const { c, g } = cvs(S);
  // sclera, darkening toward the rim (the ball curves away from the light)
  g.fillStyle = spec.sclera; g.fillRect(0, 0, S, S);
  const vig = g.createRadialGradient(c0, c0, S * 0.2, c0, c0, S * 0.5);
  vig.addColorStop(0, "rgba(0,0,0,0)");
  vig.addColorStop(1, "rgba(0,0,0,0.45)");
  g.fillStyle = vig; g.fillRect(0, 0, S, S);
  if (spec.veins) {
    for (let i = 0; i < 9; i++) {
      const a = Math.random() * Math.PI * 2;
      g.strokeStyle = "rgba(160,30,30,0.28)"; g.lineWidth = 0.8;
      g.beginPath();
      let x = c0 + Math.cos(a) * S * 0.5, y = c0 + Math.sin(a) * S * 0.5, d = a + Math.PI;
      g.moveTo(x, y);
      for (let k = 0; k < 7; k++) {
        d += (Math.random() - 0.5) * 0.9;
        x += Math.cos(d) * 3; y += Math.sin(d) * 3;
        g.lineTo(x, y);
      }
      g.stroke();
    }
  }
  const R = spec.irisR * c0;
  // iris body: a radial ramp from the pupil out to the limbus
  const ir = g.createRadialGradient(c0, c0, R * 0.1, c0, c0, R);
  ir.addColorStop(0, spec.irisIn);
  ir.addColorStop(0.55, spec.irisIn);
  ir.addColorStop(1, spec.irisOut);
  g.fillStyle = ir; g.beginPath(); g.arc(c0, c0, R, 0, 7); g.fill();
  // stroma fibres: radial streaks, light and dark — THE detail that makes an
  // iris an iris rather than a coloured disc
  for (let i = 0; i < 220; i++) {
    const a = Math.random() * Math.PI * 2;
    const r0 = R * (0.18 + Math.random() * 0.2), r1 = R * (0.6 + Math.random() * 0.38);
    g.strokeStyle = Math.random() < 0.5 ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.20)";
    g.lineWidth = 0.6 + Math.random() * 0.9;
    g.beginPath();
    g.moveTo(c0 + Math.cos(a) * r0, c0 + Math.sin(a) * r0);
    g.lineTo(c0 + Math.cos(a + (Math.random() - 0.5) * 0.12) * r1, c0 + Math.sin(a + (Math.random() - 0.5) * 0.12) * r1);
    g.stroke();
  }
  // collarette: the ragged ring a third of the way out
  g.strokeStyle = "rgba(255,255,255,0.18)"; g.lineWidth = 1.6;
  g.beginPath(); g.arc(c0, c0, R * 0.42, 0, 7); g.stroke();
  // limbal ring
  g.strokeStyle = spec.limbus; g.globalAlpha = 0.75; g.lineWidth = R * 0.12;
  g.beginPath(); g.arc(c0, c0, R * 0.95, 0, 7); g.stroke();
  g.globalAlpha = 1;
  // pupil, with a soft dark halo so its edge isn't a cut-out
  const pw = spec.pupilW * R, ph = spec.pupilH * R;
  g.fillStyle = "rgba(0,0,0,0.35)";
  g.beginPath(); g.ellipse(c0, c0, pw * 1.25, ph * 1.08, 0, 0, 7); g.fill();
  g.fillStyle = "#050404";
  g.beginPath(); g.ellipse(c0, c0, pw, ph, 0, 0, 7); g.fill();
  // a small catch-light, upper left — a wet eye always shows one
  g.fillStyle = "rgba(255,255,255,0.85)";
  g.beginPath(); g.ellipse(c0 - R * 0.32, c0 - R * 0.34, R * 0.09, R * 0.07, -0.5, 0, 7); g.fill();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  t.userData.shared = true; // module-cached: never disposed with a monster
  eyeTex[kind] = t;
  return t;
}
